import {
  Fragment,
  useState,
  useCallback,
  useEffect,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type Row,
  type RowSelectionState,
  type VisibilityState,
  type OnChangeFn,
} from '@tanstack/react-table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Search,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Sparkles,
  ClipboardList,
  Plus,
  Check,
  X,
  AlertTriangle,
} from 'lucide-react'
import { isRanAndEmpty, useExtractionStore } from '@/stores/extraction-store'
import { useDocumentsStore } from '@/stores/documents-store'
import { usePreviewStore } from '@/stores/preview-store'
import { CitationLink } from '@/components/extraction/CitationLink'
import { ExtractionPhaseStepper } from '@/components/extraction/ExtractionPhaseStepper'
import { GradientSparkleIcon } from '@/components/extraction/GradientSparkleIcon'
import { ReviewRunNotice } from '@/components/drafting/ReviewRunNotice'
import { Badge } from '@/components/ui/badge'
import {
  COMPLIANCE_STATUS_OPTIONS,
  RISK_STATUS_OPTIONS,
  SUBMISSION_STATUS_OPTIONS,
} from '@/components/extraction/StatusDropdown'
import { PARTIAL_READ_NOTICE, type ExtractionCategory } from '@/types/extraction'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

// Per-category status → human label lookup for the removed-items pile.
// Extraction items carry category-specific statuses (compliance/submission/risk);
// eval-criteria and key_dates have none. Reuse the StatusDropdown option labels
// so the pill reads exactly like the live status control; fall back to a
// humanized version of the raw value for any status not in the option list.
const CATEGORY_STATUS_LABELS: Partial<
  Record<ExtractionCategory, { value: string; label: string }[]>
> = {
  compliance_matrix: COMPLIANCE_STATUS_OPTIONS,
  submission_checklist: SUBMISSION_STATUS_OPTIONS,
  risk_register: RISK_STATUS_OPTIONS,
}

function humanizeStatus(raw: string): string {
  return raw
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// ===================
// Types
// ===================

interface ExtractionTableProps<T> {
  bidId: string
  category: ExtractionCategory
  title: string
  description: string
  columns: ColumnDef<T, unknown>[]
  data: T[]
  /**
   * The count line above the toolbar. Pass a FUNCTION to make it search-aware:
   * the search box is this component's own state, so a stats bar handed down as
   * a finished node cannot see it and will keep claiming the pre-search total
   * while the table shows fewer rows. The argument is the number of rows
   * actually rendered — filters and search both already applied.
   */
  statsBar?: React.ReactNode | ((visibleCount: number) => React.ReactNode)
  /**
   * The filter dropdowns, rendered inline in the toolbar row. Pair it with
   * `filterChips` — both come from one `useTableFilterBar()` call.
   */
  filterContent?: React.ReactNode
  /**
   * The active-filter chip rail, rendered on its own full-width row BELOW the
   * toolbar. It describes the whole table, so it starts at the toolbar's left
   * edge (under the search box) rather than under the first dropdown. Nested
   * inside `filterContent` it could only be indented under the dropdowns, and
   * the space under Search read as a gap.
   */
  filterChips?: React.ReactNode
  onExport?: () => void
  onAddManualItem?: () => void
  showAddRow?: boolean
  addRowContent?: React.ReactNode
  globalFilterFn?: (row: T, filterValue: string) => boolean
  readOnly?: boolean
  /**
   * When true, rows can be clicked to expand in-place. The row grows
   * vertically to reveal the full content of every cell (line-clamp
   * truncation is removed). Accordion behavior — only one row expanded
   * at a time. Columns stay exactly where they are.
   */
  expandableRows?: boolean
  /**
   * Optional extra content shown beneath an expanded row. When provided (and
   * `expandableRows` is on), an additional full-width row is rendered directly
   * under the expanded row and collapses with it. Omit the prop and the table
   * behaves exactly as before — no extra row is emitted.
   */
  renderExpandedContent?: (item: T) => React.ReactNode
  /**
   * Column id the expanded content should line up with. The detail row keeps
   * empty cells for the columns before it, so the browser computes the offset
   * from the real column widths — it stays correct when selection is toggled
   * or a column is resized. Omit it and the content spans the full width.
   */
  expandedContentAlignTo?: string
  /**
   * Detail-surface seam — replaces `expandableRows` for tables that render a
   * drawer. The active row is highlighted and keyboard-traversable; the caller
   * owns what the activation shows. Passing both this and `expandableRows` is a
   * programming error; activation wins and expansion is disabled.
   */
  activeRowId?: string | null
  onRowActivate?: (rowId: string | null) => void
  /**
   * When true, a leftmost checkbox column is rendered and rows become
   * multi-selectable. Selected rows are passed to `renderBulkActions`.
   */
  enableSelection?: boolean
  /**
   * Renders the bulk actions area (a floating bar). Receives the currently
   * selected rows and a function to clear the selection. Only called when
   * one or more rows are selected.
   */
  renderBulkActions?: (selectedRows: T[], clearSelection: () => void) => React.ReactNode
  /**
   * Controlled TanStack column visibility (`false` = hidden). Controlled, not
   * seeded: the caller owns the picker UI, so a later change to this prop has
   * to reach the table.
   */
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>
  /**
   * Extra toolbar controls (a Columns picker, a Group-by menu) rendered in the
   * toolbar row directly after `filterContent`.
   */
  toolbarExtras?: React.ReactNode
  /**
   * A detail surface (the row's drawer) rendered to the RIGHT of the table
   * card, inside the same row.
   *
   * The split starts at the table, not at the top of the block: the stats bar,
   * the filter rail and the toolbar above describe the whole tab, so they span
   * the full width. That is what makes the drawer and the table share one top
   * edge without anyone maintaining an offset — and it stops the filter rail
   * being squeezed every time a row is selected.
   *
   * The caller owns the node's width, stickiness and height cap; this slot only
   * places it.
   */
  detailPanel?: React.ReactNode
  /**
   * Render-level grouping — presentation only. Filtering, sorting, selection
   * and the stats bar all keep reading the ungrouped row model, so grouping
   * cannot change what a bulk action reaches or what the counts say.
   */
  groupBy?: {
    /** Stable id; collapse state resets when it changes. */
    id: string
    accessor: (item: T) => string
    /** Group keys in display order; unknown keys append in first-seen order. */
    groupOrder: string[]
    renderHeader: (groupKey: string, rows: T[]) => React.ReactNode
  } | null
}

// Interactive selector — clicks on these elements inside a row should NOT
// toggle expansion or activation (so dropdowns, menus, links, inputs stay
// functional). `[data-no-row-expand]` stays alongside the newer
// `[data-no-row-activate]` — four tables still mark their cells with it.
const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [role="menuitem"], [role="combobox"], [role="dialog"], [role="menu"], [data-no-row-expand], [data-no-row-activate]'

// Fields and overlays that own their own keys — the window-level row-traversal
// handler must not steal arrows or Escape from them.
const KEY_CAPTURE_SELECTOR =
  'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"], [role="combobox"]'

// Helper to safely read a string property from an unknown item
function getStringProp(item: unknown, key: string): string {
  if (item !== null && typeof item === 'object' && key in item) {
    const val = (item as Record<string, unknown>)[key]
    return typeof val === 'string' ? val : ''
  }
  return ''
}

// Helper to safely read a nullable value from an unknown item
function getProp<V>(item: unknown, key: string): V | null {
  if (item !== null && typeof item === 'object' && key in item) {
    return (item as Record<string, unknown>)[key] as V
  }
  return null
}

// ===================
// Component
// ===================

export function ExtractionTable<T>({
  bidId,
  category,
  title,
  description,
  columns,
  data,
  statsBar,
  filterContent,
  filterChips,
  onExport,
  onAddManualItem,
  showAddRow,
  addRowContent,
  globalFilterFn,
  readOnly,
  expandableRows,
  renderExpandedContent,
  expandedContentAlignTo,
  activeRowId,
  onRowActivate,
  enableSelection,
  renderBulkActions,
  columnVisibility,
  onColumnVisibilityChange,
  toolbarExtras,
  groupBy,
  detailPanel,
}: ExtractionTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [showReextractDialog, setShowReextractDialog] = useState(false)
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // Removed-items review pile (superseded rows from a merge re-run)
  const [removedExpanded, setRemovedExpanded] = useState(false)
  const [removeConfirmId, setRemoveConfirmId] = useState<string | null>(null)
  // In-flight guards — prevent double-POST duplicate rows from double-clicks.
  const [newItemsBusy, setNewItemsBusy] = useState(false)
  const [pendingRemovedIds, setPendingRemovedIds] = useState<Set<string>>(new Set())
  // Collapsed render-level groups, keyed by group key.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  // Collapse state belongs to the grouping it was made against: regrouping by
  // something else and inheriting "Beta is collapsed" would hide rows the user
  // never chose to hide. Reset during render rather than in an effect, so the
  // stale collapse never reaches the screen for a frame.
  const groupById = groupBy?.id ?? null
  const [prevGroupById, setPrevGroupById] = useState(groupById)
  if (prevGroupById !== groupById) {
    setPrevGroupById(groupById)
    setCollapsedGroups(new Set())
  }
  const hasActivation = !!onRowActivate
  // A row cannot both grow in place and open a drawer, so activation disables
  // expansion outright rather than letting the two fight over the same click.
  const hasExpansion = !!expandableRows && !hasActivation
  const hasSelection = !!enableSelection

  // Build a wrapped columns array with a leading checkbox column when
  // selection is enabled. Memoising isn't strictly necessary — TanStack
  // re-runs cheaply — but keeps the column array stable across renders.
  const tableColumns = hasSelection
    ? ([
        {
          id: '__select__',
          size: 36,
          header: ({ table }) => (
            <div className="flex items-center justify-center" data-no-row-expand>
              <Checkbox
                checked={
                  table.getIsAllRowsSelected()
                    ? true
                    : table.getIsSomeRowsSelected()
                      ? 'indeterminate'
                      : false
                }
                onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
                aria-label="Select all rows"
              />
            </div>
          ),
          cell: ({ row }) => (
            <div className="flex items-center justify-center" data-no-row-expand>
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
        } as ColumnDef<unknown, unknown>,
        ...(columns as ColumnDef<unknown, unknown>[]),
      ] as ColumnDef<unknown, unknown>[])
    : (columns as ColumnDef<unknown, unknown>[])

  const toggleRowExpansion = useCallback((rowId: string) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId))
  }, [])

  // Store state
  const categoryState = useExtractionStore((s) => s.categories[category])
  const hasActiveController = useExtractionStore((s) => s.abortControllers[category] !== null)
  const startExtraction = useExtractionStore((s) => s.startExtraction)
  const findNewItems = useExtractionStore((s) => s.findNewItems)
  const fullReextract = useExtractionStore((s) => s.fullReextract)
  const acceptNewItem = useExtractionStore((s) => s.acceptNewItem)
  const dismissNewItem = useExtractionStore((s) => s.dismissNewItem)
  const acceptAllNewItems = useExtractionStore((s) => s.acceptAllNewItems)
  const dismissAllNewItems = useExtractionStore((s) => s.dismissAllNewItems)
  const restoreRemovedItem = useExtractionStore((s) => s.restoreRemovedItem)
  const confirmRemoveItem = useExtractionStore((s) => s.confirmRemoveItem)

  // Documents store
  const documents = useDocumentsStore((s) => s.documents)
  const hasDocuments = documents.length > 0

  const {
    state: extractionState,
    streamingItems,
    newItems,
    isReviewingNewItems,
    isRecovering,
    streamError,
    phase,
  } = categoryState
  // Defensive default — some older store mocks omit removedItems.
  const removedItems = categoryState.removedItems ?? []

  // Table instance — cast through unknown to satisfy the strict generic constraint
  const table = useReactTable({
    data: data as unknown[],
    columns: tableColumns,
    // Visibility is spread in only when the caller drives it — an unconditional
    // `columnVisibility: undefined` would still hand TanStack a controlled slot
    // and freeze it, so an omitted prop must leave the key off entirely.
    state: {
      sorting,
      globalFilter,
      rowSelection,
      ...(columnVisibility ? { columnVisibility } : {}),
    },
    ...(onColumnVisibilityChange ? { onColumnVisibilityChange } : {}),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: hasSelection,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Stable row IDs so expansion state survives sorting/filtering.
    // Prefer the item's own id; fall back to index for unsaved items.
    getRowId: (row: unknown, index: number) => {
      const obj = row as { id?: string | null }
      return obj?.id ?? `__row_${index}`
    },
    ...(globalFilterFn
      ? {
          globalFilterFn: (row: Row<unknown>, _columnId: string, filterValue: string) =>
            globalFilterFn(row.original as T, filterValue),
        }
      : {}),
  })

  /**
   * Drop selection for rows the user can no longer see.
   *
   * Two different causes, and they deserve different treatment. A row that has
   * GONE — deleted, or dropped by a refresh — cannot be brought back by
   * widening the view, so removing it silently is right. A row that is merely
   * HIDDEN by the filters or the search is still there, and dropping it in
   * silence is how a bulk action ends up landing on fewer rows than the user
   * lined up: tick twenty, refine the search, act on the remainder without ever
   * being told. Pruning is still correct — an action must never reach a row
   * that is off screen — but it has to say so.
   *
   * `getRowModel()` is post-filter and `getCoreRowModel()` is not, so the
   * difference between them is exactly "hidden, not gone".
   */
  useEffect(() => {
    if (!hasSelection) return
    const visibleIds = new Set(table.getRowModel().rows.map((r) => r.id))
    const existingIds = new Set(table.getCoreRowModel().rows.map((r) => r.id))

    let hiddenCount = 0
    let dirty = false
    const next: RowSelectionState = {}
    for (const id of Object.keys(rowSelection)) {
      if (visibleIds.has(id)) {
        next[id] = rowSelection[id]!
        continue
      }
      dirty = true
      if (existingIds.has(id)) hiddenCount++
    }
    if (!dirty) return
    setRowSelection(next)
    if (hiddenCount > 0) {
      toast.info(
        hiddenCount === 1
          ? '1 selected row is hidden by the current view and was deselected'
          : `${hiddenCount} selected rows are hidden by the current view and were deselected`
      )
    }
    // `globalFilter` is a real dependency: `table` is a stable instance, and
    // `data`/`rowSelection` do not change when the user types, so without it
    // this never re-ran on a search — the prune only happened when the rows
    // themselves changed.
  }, [data, hasSelection, rowSelection, table, globalFilter])

  /**
   * Row traversal while a detail surface is open.
   *
   * Window-level, not row-level: once the caller's drawer takes focus the rows
   * no longer have it, and a handler bound to the row would go deaf exactly
   * when the user starts reading. Only armed while a row is active, so a table
   * with nothing open never touches the page's keys.
   *
   * Traversal walks `getRowModel()` — the sorted, filtered order the user is
   * actually looking at, not the raw data order.
   */
  useEffect(() => {
    if (!hasActivation || activeRowId == null) return
    const handler = (e: globalThis.KeyboardEvent) => {
      // `instanceof Element`, not a cast: a keydown dispatched at `window` has
      // a target with no `closest`, and a cast would crash on it.
      const target = e.target instanceof Element ? e.target : null
      if (target?.closest(KEY_CAPTURE_SELECTOR)) return
      if (e.key === 'Escape') {
        // The docked reader claims Esc while a preview is open.
        if (usePreviewStore.getState().isOpen) return
        e.preventDefault()
        onRowActivate?.(null)
        return
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      const rows = table.getRowModel().rows
      const idx = rows.findIndex((r) => r.id === activeRowId)
      if (idx === -1) return
      const next = rows[e.key === 'ArrowDown' ? idx + 1 : idx - 1]
      if (!next) return
      e.preventDefault()
      onRowActivate?.(next.id)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [hasActivation, activeRowId, onRowActivate, table])

  const selectedRows = hasSelection
    ? (table.getSelectedRowModel().rows.map((r) => r.original) as T[])
    : []
  const clearSelection = useCallback(() => setRowSelection({}), [])

  const handleStartExtraction = useCallback(() => {
    void startExtraction(bidId, category)
  }, [bidId, category, startExtraction])

  const handleFindNewItems = useCallback(() => {
    void findNewItems(bidId, category)
  }, [bidId, category, findNewItems])

  const handleFullReextract = useCallback(() => {
    setShowReextractDialog(false)
    void fullReextract(bidId, category)
  }, [bidId, category, fullReextract])

  // New-items actions guarded by a single in-flight flag — a double-click on
  // Accept would otherwise fire two POSTs and create duplicate rows.
  const runNewItemsAction = useCallback(async (fn: () => Promise<void>) => {
    setNewItemsBusy(true)
    try {
      await fn()
    } finally {
      setNewItemsBusy(false)
    }
  }, [])

  // Removed-items actions guarded per-id so restoring one row doesn't lock
  // the others.
  const runRemovedAction = useCallback(async (itemId: string, fn: () => Promise<void>) => {
    setPendingRemovedIds((prev) => new Set(prev).add(itemId))
    try {
      await fn()
    } finally {
      setPendingRemovedIds((prev) => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }
  }, [])

  // ========================================
  // EMPTY STATE
  // ========================================
  // A zero-item run is finalized as `populated` with `items: []` (see
  // `finalizeRun`), so `state === 'empty'` alone never reaches the
  // "ran and found nothing" copy below — the user would click through the
  // collapsed tab into a blank table with no explanation and no way to re-run.
  // Widen on the SAME selector that collapses the tab so the two can't disagree.
  //
  // Categories that still have something to review (pending new items, or
  // removed rows the user can restore) keep the table shell — the empty state
  // has nowhere to put those affordances.
  const hasReviewAffordances =
    !readOnly && ((isReviewingNewItems && newItems.length > 0) || removedItems.length > 0)
  if (
    extractionState === 'empty' ||
    (isRanAndEmpty(categoryState) && !hasReviewAffordances)
  ) {
    if (isRecovering || hasActiveController) {
      // Stream dropped but Lambda may still be running — keep the phase
      // stepper visible so the user sees uninterrupted "AI is working"
      // feedback rather than a different-looking spinner. The last reported
      // phase stays highlighted; if the stream never sent one, the stepper
      // defaults to the first phase.
      //
      // We also fall through here when an abortController is still live even
      // if isRecovering is somehow lost — defensive against races between
      // loadCategory and the streaming pipeline.
      return (
        <ExtractionPhaseStepper
          category={category}
          phase={phase}
          sweepProgress={categoryState.sweepProgress}
          isSweeping={categoryState.isSweeping}
        />
      )
    }

    if (streamError) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
          <p className="text-sm font-medium">Analysis couldn&rsquo;t be completed</p>
          {/*
            The run's own message, not fixed copy: an async sweep can fail
            server-side (e.g. the worker's heartbeat stopped), and telling the
            user their connection dropped would be the wrong explanation for a
            failure that had nothing to do with their connection.
          */}
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            {streamError}
          </p>
          {/*
            HEDGED ON PURPOSE — do not "tighten" this to "Nothing was saved".
            On the inline path persistence happens server-side BEFORE items
            stream to the client, so a stream that drops after the save means
            rows WERE written. The async sweep has the same hole in miniature: a
            crash between persist and complete() leaves the reaper to fail a run
            whose rows already landed. Neither path can honestly promise the
            save didn't happen — only that we can't confirm it did.
          */}
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            We couldn&rsquo;t confirm results were saved. Retry to re-run the{' '}
            {title.toLowerCase()} analysis.
          </p>
          {!readOnly && (
            <Button
              variant="outline"
              className="gap-2 mt-4"
              onClick={handleStartExtraction}
              disabled={!hasDocuments}
            >
              <RefreshCw className="h-4 w-4" />
              Retry extraction
            </Button>
          )}
        </div>
      )
    }

    // Use the shared selector rather than re-deriving "ran and found nothing"
    // inline, so this copy can never disagree with the tab-collapse decision in
    // AnalysisSection or the re-run gating in the Analyse dialog.
    const ranAndFoundNothing = isRanAndEmpty(categoryState)

    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <ClipboardList className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="text-sm font-medium">
          {ranAndFoundNothing ? 'Nothing found' : `No ${title.toLowerCase()} yet`}
        </p>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">
          {ranAndFoundNothing
            ? `The analysis ran and this tender pack does not define any ${title.toLowerCase()}.`
            : description}
        </p>
        {!readOnly && (
          <Button
            className={cn('gap-2 mt-4', !hasDocuments && 'opacity-50')}
            onClick={handleStartExtraction}
            disabled={!hasDocuments}
            title={!hasDocuments ? 'Upload documents first' : undefined}
          >
            <GradientSparkleIcon className="h-4 w-4" />
            {ranAndFoundNothing ? 'Run again' : `Extract ${title}`}
          </Button>
        )}
      </div>
    )
  }

  // ========================================
  // EXTRACTING STATE
  // ========================================
  if (extractionState === 'extracting') {
    // While the LLM/post-processing is still running we show a phase stepper
    // (no items yet to render). Once the drip-feed starts releasing items into
    // streamingItems, switch to the row preview so users see real results.
    if (streamingItems.length === 0) {
      return (
        <ExtractionPhaseStepper
          category={category}
          phase={phase}
          sweepProgress={categoryState.sweepProgress}
          isSweeping={categoryState.isSweeping}
        />
      )
    }
    return (
      <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
        {/* Header — same gradient sparkle + shimmer treatment as the stepper */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <div className="flex items-center gap-2.5">
            <span className="relative inline-flex items-center justify-center h-5 w-5">
              <span
                className="absolute inset-0 rounded-full animate-breathe"
                style={{
                  background:
                    'linear-gradient(135deg, hsl(var(--brand) / 0.3), hsl(var(--ai-grad-to) / 0.3))',
                }}
              />
              <Sparkles className="relative h-3.5 w-3.5 text-[hsl(var(--ai-accent))]" />
            </span>
            <span className="text-sm animate-shimmer">
              Loading results · {streamingItems.length} item
              {streamingItems.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>

        {/* Streaming rows */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-muted/50 border-b">
                {columns.map((col, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left text-xs font-medium text-muted-foreground"
                  >
                    {typeof col.header === 'string' ? col.header : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {streamingItems.map((item, i) => (
                <tr key={i} className="animate-fade-in">
                  {columns.map((_, colIdx) => (
                    <td key={colIdx} className="px-4 py-2">
                      {colIdx === 0 && (
                        <span className="text-meta">{i + 1}</span>
                      )}
                      {colIdx === 1 && 'title' in item && (
                        <span className="text-sm">{getStringProp(item, 'title')}</span>
                      )}
                      {colIdx === 1 && 'event' in item && (
                        <span className="text-sm">{getStringProp(item, 'event')}</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
              {/* Shimmer placeholder rows — only while the drip is still running */}
              {Array.from({ length: 3 }).map((_, i) => (
                <tr key={`shimmer-${i}`}>
                  <td colSpan={columns.length} className="px-4 py-3">
                    <div
                      className="h-4 rounded animate-shimmer-bg"
                      style={{
                        width: `${70 - i * 15}%`,
                        background:
                          'linear-gradient(90deg, hsl(var(--muted)) 0%, hsl(var(--brand) / 0.18) 50%, hsl(var(--muted)) 100%)',
                        backgroundSize: '200% 100%',
                        animationDelay: `${i * 0.25}s`,
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // ========================================
  // POPULATED STATE
  // ========================================
  const rows = table.getRowModel().rows

  /**
   * Render-level grouping — a partition of the rows the table was already
   * going to render, nothing more. `getRowModel()` is untouched, so filtering,
   * sorting, selection, the selection-prune effect and the stats count all
   * behave identically whether grouping is on or off.
   *
   * `gb` is a const so its narrowing survives into the closure below; the
   * `groupBy` prop binding would not narrow there.
   */
  const gb = groupBy
  const groups = gb
    ? (() => {
        const byKey = new Map<string, typeof rows>()
        for (const row of rows) {
          const key = gb.accessor(row.original as T)
          const bucket = byKey.get(key) ?? []
          bucket.push(row)
          byKey.set(key, bucket)
        }
        const ordered = [
          ...gb.groupOrder.filter((k) => byKey.has(k)),
          // Anything the caller's order does not name still has to appear —
          // dropping it would hide rows the filters kept.
          ...[...byKey.keys()].filter((k) => !gb.groupOrder.includes(k)),
        ]
        return ordered.map((key) => {
          const bucket = byKey.get(key)!
          return {
            key,
            rows: bucket,
            header: gb.renderHeader(key, bucket.map((r) => r.original as T)),
          }
        })
      })()
    : null

  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const renderRow = (row: Row<unknown>) => {
    const rowId = row.id
    const isExpanded = hasExpansion && expandedRowId === rowId
    const isActive = hasActivation && activeRowId === rowId

    const handleActivateClick = hasActivation
      ? (e: MouseEvent<HTMLTableRowElement>) => {
          const target = e.target as HTMLElement
          const interactive = target.closest(INTERACTIVE_SELECTOR)
          if (interactive && interactive !== e.currentTarget) return
          onRowActivate?.(isActive ? null : rowId)
        }
      : undefined

    const handleActivateKeyDown = hasActivation
      ? (e: KeyboardEvent<HTMLTableRowElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          if (e.target !== e.currentTarget) return
          e.preventDefault()
          onRowActivate?.(isActive ? null : rowId)
        }
      : undefined

    const handleRowClick = hasExpansion
      ? (e: MouseEvent<HTMLTableRowElement>) => {
          const target = e.target as HTMLElement
          const interactive = target.closest(INTERACTIVE_SELECTOR)
          // Ignore only if the interactive ancestor is inside the row
          // (not the row itself, which also has role="button").
          if (interactive && interactive !== e.currentTarget) return
          toggleRowExpansion(rowId)
        }
      : undefined

    const handleRowKeyDown = hasExpansion
      ? (e: KeyboardEvent<HTMLTableRowElement>) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          // Only toggle when the row itself has focus, not a child input
          if (e.target !== e.currentTarget) return
          e.preventDefault()
          toggleRowExpansion(rowId)
        }
      : undefined

    const visibleCells = row.getVisibleCells()

    return (
      <Fragment key={rowId}>
        <tr
          data-row-id={rowId}
          data-expanded={isExpanded ? 'true' : undefined}
          data-active={isActive ? 'true' : undefined}
          className={cn(
            'transition-colors group hover:bg-accent/30',
            // These rows take `tabIndex={0}` + `role="button"` below, so they
            // are real keyboard stops — but they carried no focus style, and a
            // focusable element with none falls through to the BROWSER default
            // (Chrome paints its own `#005FCC` ring). That default is what
            // showed up in a screenshot and got mistaken for one of our tokens.
            // Outline rather than box-shadow deliberately: `isActive` already
            // owns the row's `shadow-[inset...]`, and the two would fight.
            (hasExpansion || hasActivation) &&
              'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[hsl(var(--ring))]',
            // Inset shadow, not `border-l`: `border-collapse` makes
            // tr borders unreliable across browsers.
            isActive && 'bg-accent/40 shadow-[inset_2px_0_0_hsl(var(--brand))]',
            isExpanded && [
              'bg-accent/20',
              // Override line-clamp utilities inside the expanded row
              // so the cell content grows vertically in place. Vertical
              // alignment is intentionally NOT changed — switching from
              // baseline to top would shift single-line cells upward
              // even on rows that don't actually need to grow.
              '[&_.line-clamp-1]:!line-clamp-none',
              '[&_.line-clamp-2]:!line-clamp-none',
              '[&_.line-clamp-3]:!line-clamp-none',
            ],
          )}
          {...(hasExpansion && {
            onClick: handleRowClick,
            onKeyDown: handleRowKeyDown,
            tabIndex: 0,
            role: 'button',
            'aria-expanded': isExpanded,
            'aria-label': isExpanded
              ? 'Collapse row'
              : 'Expand row to see full details',
          })}
          {...(hasActivation && {
            onClick: handleActivateClick,
            onKeyDown: handleActivateKeyDown,
            tabIndex: 0,
            role: 'button',
            // `aria-pressed`, not `aria-selected`: ARIA does not
            // support `aria-selected` on `role="button"`, so a
            // reader would drop the state entirely. Set on every
            // activatable row so the toggle reads as a real pair.
            'aria-pressed': isActive,
            'aria-label': isActive ? 'Close row details' : 'Open row details',
          })}
        >
          {visibleCells.map((cell) => (
            <td key={cell.id} className={cn(
              "py-2",
              cell.column.id === '__select__'
                ? 'pl-3 pr-1'
                : cell.column.id === 'number' || cell.column.id === 'req'
                  ? 'pl-4 pr-1'
                  : 'px-4'
            )}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
        {/* Optional caller-supplied detail row. `!border-t-0` beats the
            tbody's `divide-y` so it reads as a continuation of the row
            above rather than a new row. */}
        {isExpanded && renderExpandedContent && (() => {
          // Spacer cells for the columns before the alignment target, so
          // the content starts on the same vertical line as that column's
          // text. -1 (not found, or no target) means span everything.
          const alignIdx = expandedContentAlignTo
            ? visibleCells.findIndex((c) => c.column.id === expandedContentAlignTo)
            : -1
          const leading = alignIdx > 0 ? visibleCells.slice(0, alignIdx) : []
          return (
            <tr data-testid="expanded-content-row" className="bg-accent/20 !border-t-0">
              {leading.map((cell) => (
                <td key={`spacer-${cell.id}`} aria-hidden className="p-0" />
              ))}
              <td
                colSpan={visibleCells.length - leading.length}
                className="px-4 pb-3 pt-0"
              >
                {renderExpandedContent(row.original as T)}
              </td>
            </tr>
          )
        })()}
      </Fragment>
    )
  }

  return (
    <div className="space-y-3">
      {/*
        Failed re-run over an existing table. The rows below are the PREVIOUS
        run's and are still the customer's work — the failure is reported over
        them, never in place of them.
      */}
      {streamError && (
        <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">Analysis couldn&rsquo;t be completed</p>
            {/*
              "may be from an earlier analysis", not "are". On the inline path a
              run can persist server-side and then lose its stream, so the rows
              below can be THIS run's — asserting they're the previous run's
              would be flatly wrong exactly when the user is deciding whether to
              retry. Hedged copy is true on both the inline and sweep paths.
            */}
            <p className="text-sm text-muted-foreground mt-0.5">
              {streamError} The {title.toLowerCase()} below may be from an earlier analysis.
            </p>
          </div>
          {!readOnly && (
            <Button variant="ghost" size="sm" className="h-8 gap-1.5" onClick={handleStartExtraction}>
              <RefreshCw className="h-4 w-4" />
              Retry
            </Button>
          )}
        </div>
      )}

      {/*
        Partial read: the run could not read the AI's whole answer, so it saved
        what it read and KEPT the rows it did not reach. Said over the rows so a
        short result never passes for the whole answer.
      */}
      {categoryState.partialRead && <ReviewRunNotice message={PARTIAL_READ_NOTICE} />}

      {/* Stats bar */}
      {typeof statsBar === 'function' ? statsBar(table.getRowModel().rows.length) : statsBar}

      {/* Toolbar, and the active-filter rail beneath it */}
      {/*
        A column, so the chip rail gets its OWN full-width row starting at the
        toolbar's left edge — under the search box, not indented under the first
        dropdown. `gap-2` keeps it 8px below the controls, the same 8px it had
        while it was nested inside `filterContent`.
      */}
      <div className="flex flex-col gap-2">
        {/*
          `items-start`, not `items-center`: every direct child is h-8 today, so
          the two render identically — but a control that grows taller should
          extend the row downward rather than drag Search and the trailing
          buttons down to its midline.
        */}
        <div className="flex items-start gap-2 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              className="pl-8 h-8"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>

          {/* Per-tab filters */}
          {filterContent}

          {/* Caller-supplied toolbar controls (Columns picker, Group by) */}
          {toolbarExtras}

          <div className="flex-1" />

          {/* Add manual item */}
          {!readOnly && onAddManualItem && (
            <Button variant="ghost" size="sm" onClick={onAddManualItem} className="h-8">
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          )}

          {/* Export */}
          {onExport && (
            <Button variant="ghost" size="sm" onClick={onExport} className="h-8">
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
          )}

          {/* Re-extract dropdown */}
          {!readOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ai-ghost" size="sm" className="h-8">
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Re-extract
                  <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleFindNewItems}>
                  Find new items
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowReextractDialog(true)}>
                  Re-run analysis
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* The rail — a sibling of the toolbar row, never a child of it. */}
        {filterChips}
      </div>

      {/* New items review banner */}
      {!readOnly && isReviewingNewItems && newItems.length > 0 && (
        <div className="border rounded-lg bg-amber-50/50">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="text-sm font-medium">
              {newItems.length} new item{newItems.length !== 1 ? 's' : ''} found
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={newItemsBusy}
                onClick={() => void runNewItemsAction(() => acceptAllNewItems(bidId, category))}
                className="h-7"
              >
                Accept All
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={newItemsBusy}
                onClick={() => void runNewItemsAction(() => dismissAllNewItems(bidId, category))}
                className="h-7"
              >
                Dismiss
              </Button>
            </div>
          </div>
          {newItems.map((item, i) => {
            const itemLabel =
              'title' in item
                ? getStringProp(item, 'title')
                : 'event' in item
                  ? getStringProp(item, 'event')
                  : ''
            return (
              <div key={i} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0">
                <span className="text-sm flex-1">{itemLabel}</span>
                {'sourcePage' in item && 'sourceDocumentId' in item && (
                  <CitationLink
                    page={getProp<number>(item, 'sourcePage')}
                    documentId={getProp<string>(item, 'sourceDocumentId')}
                  />
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  aria-label={itemLabel ? `Accept "${itemLabel}"` : 'Accept new item'}
                  disabled={newItemsBusy}
                  onClick={() => void runNewItemsAction(() => acceptNewItem(bidId, category, i))}
                >
                  <Check className="h-4 w-4 text-green-600" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  aria-label={itemLabel ? `Dismiss "${itemLabel}"` : 'Dismiss new item'}
                  disabled={newItemsBusy}
                  onClick={() => void runNewItemsAction(() => dismissNewItem(bidId, category, i))}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {/* Table, and the detail drawer beside it.

          The split starts HERE, below the toolbar, rather than wrapping the
          whole block: the stats bar, filters and toolbar above span the full
          width, so the table card and the drawer share one top edge with no
          measured offset to maintain, and selecting a row no longer reflows
          the filter rail.

          Keep a 280px floor where the parent has room, so a docked document
          reader cannot squeeze the table away. Narrow phone panes cap that
          floor at their available width; the table body scrolls its columns. */}
      <div className="flex items-start gap-3">
        <div className="min-w-[min(280px,100%)] flex-1">
          {/* Table — the card clips vertically (rounded corners); the inner
              wrapper scrolls horizontally so wide column sets stay reachable
              instead of being cut off at the card edge. */}
          <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
            <div
              role="region"
              aria-label={`${title} table`}
              tabIndex={0}
              className="overflow-x-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[hsl(var(--brand))]"
            >
              <table className="w-full">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id} className="bg-muted/50 border-b">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            "py-3 text-left text-xs font-medium text-muted-foreground",
                            header.column.id === '__select__'
                              ? 'pl-3 pr-1'
                              : header.column.id === 'number' || header.column.id === 'req'
                                ? 'pl-4 pr-1'
                                : 'px-4'
                          )}
                          style={
                            header.column.columnDef.size
                              ? { width: header.column.columnDef.size }
                              : undefined
                          }
                          aria-sort={
                            header.column.getIsSorted() === 'asc'
                              ? 'ascending'
                              : header.column.getIsSorted() === 'desc'
                                ? 'descending'
                                : undefined
                          }
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y">
                  {groups
                    ? groups.map((group) => {
                        const isCollapsed = collapsedGroups.has(group.key)
                        return (
                          <Fragment key={`__group_${group.key}`}>
                            <tr data-testid="group-header" className="bg-muted/30">
                              <td
                                colSpan={table.getVisibleLeafColumns().length}
                                className="px-2 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 shrink-0"
                                    aria-expanded={!isCollapsed}
                                    aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} group ${group.key}`}
                                    onClick={() => toggleGroup(group.key)}
                                  >
                                    <ChevronRight
                                      className={cn(
                                        'h-4 w-4 text-muted-foreground transition-transform',
                                        !isCollapsed && 'rotate-90'
                                      )}
                                    />
                                  </Button>
                                  {group.header}
                                </div>
                              </td>
                            </tr>
                            {!isCollapsed && group.rows.map(renderRow)}
                          </Fragment>
                        )
                      })
                    : rows.map(renderRow)}
                  {/* Inline add row */}
                  {showAddRow && addRowContent}
                </tbody>
              </table>
            </div>
            {table.getRowModel().rows.length === 0 && !showAddRow && (
              <div className="text-center py-12 text-sm text-muted-foreground">
                {/* This block only renders when the category HAS items (the
                    no-data states return earlier), so zero rows here means the
                    user narrowed them away. Name the control that did it —
                    blaming the search box when the search box is empty sends
                    the user looking in the wrong place. */}
                {globalFilter.trim()
                  ? 'No items match your search.'
                  : 'No items match the filters you have applied.'}
              </div>
            )}
          </div>
        </div>
        {detailPanel}
      </div>

      {/* Removed-items review pile — superseded rows from the latest re-run.
          Preserved workflow status is kept; the user restores or permanently
          removes each row from re-run matching. */}
      {!readOnly && removedItems.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <button
            type="button"
            onClick={() => setRemovedExpanded((v) => !v)}
            aria-expanded={removedExpanded}
            className="flex w-full items-center gap-2 bg-muted/50 px-3 py-2 text-left hover:bg-muted/70 transition-colors"
          >
            <ChevronRight
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                removedExpanded && 'rotate-90'
              )}
            />
            <span className="text-sm font-medium">
              Removed by latest analysis ({removedItems.length})
            </span>
            <span className="text-meta">
              No longer found in the tender — restore or remove.
            </span>
          </button>
          {removedExpanded && (
            <div className="divide-y">
              {removedItems.map((item) => {
                const itemId = getStringProp(item, 'id')
                const label =
                  getStringProp(item, 'title') || getStringProp(item, 'event') || 'Untitled'
                const rawStatus = getStringProp(item, 'status')
                const statusLabel = rawStatus
                  ? (CATEGORY_STATUS_LABELS[category]?.find((o) => o.value === rawStatus)?.label ??
                    humanizeStatus(rawStatus))
                  : null
                const busy = pendingRemovedIds.has(itemId)
                return (
                  <div key={itemId} className="flex items-center gap-2 px-3 py-2">
                    <span className="flex-1 text-sm line-through text-muted-foreground">
                      {label}
                    </span>
                    {statusLabel && (
                      <Badge variant="secondary" dot className="text-muted-foreground">
                        {statusLabel}
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-7"
                      disabled={busy || !itemId}
                      onClick={() =>
                        void runRemovedAction(itemId, () =>
                          restoreRemovedItem(bidId, category, itemId)
                        )
                      }
                    >
                      Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      disabled={busy || !itemId}
                      onClick={() => setRemoveConfirmId(itemId)}
                    >
                      Remove
                    </Button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Bulk actions floating bar */}
      {hasSelection && renderBulkActions && selectedRows.length > 0 && (
        renderBulkActions(selectedRows, clearSelection)
      )}

      {/* Re-run analysis confirmation dialog — honest merge semantics */}
      <AlertDialog open={showReextractDialog} onOpenChange={setShowReextractDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-run analysis — {title}</AlertDialogTitle>
            <AlertDialogDescription>
              Existing items are updated in place — your statuses, notes, owners and section links
              are preserved. Items no longer found in the tender move to 'Removed by analysis' for
              your review. Nothing is deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleFullReextract}>Re-run analysis</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent-remove confirmation for a superseded row */}
      <AlertDialog
        open={removeConfirmId !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveConfirmId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this item?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the item permanently from re-run matching — it will not be re-proposed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const id = removeConfirmId
                setRemoveConfirmId(null)
                if (id) {
                  void runRemovedAction(id, () => confirmRemoveItem(bidId, category, id))
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
